import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { contentService } from '../../../services/contentService';

const ContentEditor = ({ selectedItem, onSave, onCancel, onDelete }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    type: 'image',
    thumbnail: '',
    thumbnailAlt: '',
    thumbnailUrl: '',
    videoUrl: '',
    price: 0,
    watermark: false,
    watermarkPosition: 'bottom-right',
    watermarkOpacity: 80,
    displayOrder: 0,
    rating: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (selectedItem) {
      setFormData({
        title: selectedItem?.title || '',
        description: selectedItem?.description || '',
        category: selectedItem?.category || '',
        type: selectedItem?.type || 'image',
        thumbnail: selectedItem?.thumbnail || '',
        thumbnailAlt: selectedItem?.thumbnailAlt || '',
        thumbnailUrl: selectedItem?.thumbnailUrl || selectedItem?.thumbnail || '',
        videoUrl: selectedItem?.videoUrl || selectedItem?.thumbnail || '', // Use thumbnail if videoUrl empty (for Hostinger URLs)
        price: selectedItem?.price || 0,
        watermark: selectedItem?.watermark || false,
        watermarkPosition: selectedItem?.watermarkPosition || 'bottom-right',
        watermarkOpacity: selectedItem?.watermarkOpacity || 80,
        displayOrder: selectedItem?.displayOrder || 0,
        rating: selectedItem?.rating || 0
      });
    }
  }, [selectedItem]);
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData?.title || !formData?.category) {
      setError('Título e categoria são obrigatórios');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Include all fields that can be updated, including thumbnailUrl
      const updatePayload = {
        title: formData?.title,
        description: formData?.description,
        category: formData?.category,
        price: formData?.price,
        videoUrl: formData?.videoUrl,
        thumbnailUrl: formData?.thumbnailUrl,
        watermark: formData?.watermark,
        watermarkPosition: formData?.watermarkPosition,
        watermarkOpacity: formData?.watermarkOpacity,
        displayOrder: formData?.displayOrder,
        format: formData?.format
      };

      const updatedItem = await contentService?.update(selectedItem?.id, updatePayload);
      onSave(updatedItem);
    } catch (err) {
      setError(err?.message || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await contentService?.delete(selectedItem?.id);
      setShowDeleteConfirm(false);
      if (onDelete) {
        onDelete(selectedItem?.id);
      }
      onCancel();
    } catch (err) {
      setError(err?.message || 'Erro ao excluir');
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = [
    { value: 'movies-series', label: 'Filmes e Séries' },
    { value: 'bbb', label: 'Big Brother Brasil' },
    { value: 'brasileirao', label: 'Brasileirão' },
    { value: 'app-partnerships', label: 'Parcerias de Apps' }
  ];

  const watermarkPositionOptions = [
    { value: 'top-left', label: 'Superior Esquerdo' },
    { value: 'top-right', label: 'Superior Direito' },
    { value: 'bottom-left', label: 'Inferior Esquerdo' },
    { value: 'bottom-right', label: 'Inferior Direito' },
    { value: 'center', label: 'Centro' }
  ];

  if (!selectedItem) {
    return (
      <div className="flex flex-col h-full bg-card rounded-lg glow-primary">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Icon name="FileEdit" size={32} color="var(--color-muted-foreground)" />
            </div>
            <h3 className="text-lg font-heading font-bold text-foreground mb-2">
              Nenhum item selecionado
            </h3>
            <p className="text-sm text-muted-foreground">
              Selecione um item da biblioteca para editar
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-card rounded-lg glow-primary">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-accent/10 flex items-center justify-center glow-accent">
              <Icon name="FileEdit" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-accent">
                Editor de Conteúdo
              </h2>
              <p className="text-xs md:text-sm text-muted-foreground">
                Edite os detalhes do item selecionado
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onCancel}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500 rounded-md text-sm text-red-500">
            {error}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 md:p-6">
        <div className="space-y-6">
          <div className="aspect-video rounded-lg overflow-hidden bg-muted">
            {formData?.type === 'video' && formData?.videoUrl ? (
              <video 
                src={formData?.videoUrl} 
                className="w-full h-full object-cover"
                controls
                playsInline
                preload="metadata"
              />
            ) : formData?.thumbnailUrl ? (
              <img
                src={formData?.thumbnailUrl}
                alt={formData?.thumbnailAlt || 'Preview'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name="Image" size={48} color="var(--color-muted-foreground)" />
              </div>
            )}
          </div>

          <div className="space-y-4">
            <Input
              label="Título"
              value={formData?.title}
              onChange={(e) => handleChange('title', e?.target?.value)}
              placeholder="Digite o título do conteúdo"
              required
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Descrição
              </label>
              <textarea
                value={formData?.description}
                onChange={(e) => handleChange('description', e?.target?.value)}
                placeholder="Digite uma descrição detalhada"
                rows={4}
                className="w-full px-4 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-smooth resize-none"
              />
            </div>

            <Select
              label="Categoria"
              value={formData?.category}
              onChange={(e) => handleChange('category', e?.target?.value)}
              options={categoryOptions}
              required
            />

            <Input
              label="Preço (R$)"
              type="number"
              value={formData?.price}
              onChange={(e) => handleChange('price', parseFloat(e?.target?.value) || 0)}
              placeholder="0.00"
              min="0"
              step="0.01"
            />

            <Input
              label="Ordem de Exibição"
              type="number"
              value={formData?.displayOrder}
              onChange={(e) => handleChange('displayOrder', parseInt(e?.target?.value) || 0)}
              placeholder="0"
              min="0"
            />

            <div className="pt-4 border-t border-border">
              <h3 className="text-sm font-heading font-bold text-foreground mb-4">
                Configurações de Marca d'água
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Checkbox
                    label="Aplicar marca d'água"
                    checked={formData?.watermark}
                    onChange={(e) => handleChange('watermark', e?.target?.checked)}
                  />
                  {formData?.watermark && (
                    <img 
                      src="/assets/images/up_menor-1769135382338.png" 
                      alt="UP MIDIA Logo" 
                      className="h-10 w-auto object-contain"
                    />
                  )}
                </div>

                {formData?.watermark && (
                  <>
                    <Select
                      label="Posição"
                      value={formData?.watermarkPosition}
                      onChange={(e) => handleChange('watermarkPosition', e?.target?.value)}
                      options={watermarkPositionOptions}
                    />

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Opacidade: {formData?.watermarkOpacity}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={formData?.watermarkOpacity}
                        onChange={(e) => handleChange('watermarkOpacity', parseInt(e?.target?.value))}
                        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 border-t border-border">
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            iconName="X"
            onClick={onCancel}
            className="flex-1"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            iconName="Trash2"
            onClick={() => setShowDeleteConfirm(true)}
            className="flex-1"
            disabled={loading}
          >
            Excluir
          </Button>
          <Button
            variant="default"
            iconName="Save"
            onClick={handleSave}
            className="flex-1"
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-card rounded-lg glow-primary p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} color="#ef4444" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground">
                  Confirmar Exclusão
                </h3>
                <p className="text-sm text-muted-foreground">
                  Esta ação não pode ser desfeita
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-6">
              Tem certeza que deseja excluir <strong>{selectedItem?.title}</strong>?
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="flex-1"
                disabled={loading}
              >
                {loading ? 'Excluindo...' : 'Excluir'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentEditor;